import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TIER_COLORS, TIER_LABEL } from "@/data/brand";
import DashboardListingCard from "@/components/DashboardListingCard";
import BoostBanner from "@/components/BoostBanner";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/listings/mine").then((r) => setListings(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    setListings((ls) => ls.filter((l) => l.id !== id));
  };

  const cancelSub = async () => {
    if (!window.confirm("Cancellare l'abbonamento? Resterà attivo fino alla scadenza, poi non verrà rinnovato.")) return;
    setCancelling(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Abbonamento cancellato. Resta attivo fino alla scadenza.");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setCancelling(false); }
  };
  const resumeSub = async () => {
    try {
      await api.post("/subscription/resume");
      toast.success("Rinnovo automatico riattivato");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const openBillingPortal = async () => {
    try {
      const { data } = await api.post("/payments/billing-portal");
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile aprire il portale clienti");
    }
  };

  const sub = user?.subscription;
  const active = sub && new Date(sub.expires_at) > new Date();
  const daysLeft = active ? Math.ceil((new Date(sub.expires_at) - new Date()) / (1000 * 60 * 60 * 24)) : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12" data-testid="dashboard-page">
      <header className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#FFBE00] mb-2">· Dashboard</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl leading-tight">Ciao, <span className="text-[#E619A8]">{user?.name}</span>.</h1>
        </div>
        <Link to="/dashboard/nuovo" className="px-5 py-3 rounded-md bg-[#FFBE00] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#E619A8] hover:text-white transition-all" data-testid="dashboard-new-btn">
          <i className="fas fa-plus mr-2"></i> Nuovo annuncio
        </Link>
        <Link to="/dashboard/impostazioni" className="px-5 py-3 rounded-md border border-zinc-700 text-white font-bold uppercase tracking-wider text-sm hover:border-[#FFBE00] hover:text-[#FFBE00] transition-all" data-testid="dashboard-settings-btn">
          <i className="fas fa-gear mr-2"></i> Impostazioni
        </Link>
      </header>

      {!user?.email_verified && (
        <div className="hm-card p-4 mb-6 border-[#E619A8] flex items-center justify-between flex-wrap gap-3" data-testid="verify-banner">
          <div className="text-sm"><i className="fas fa-triangle-exclamation text-[#E619A8] mr-2"></i>La tua email non è ancora verificata. Controlla la casella oppure richiedi un nuovo link dalle <Link to="/dashboard/impostazioni" className="text-[#FFBE00] hover:underline">Impostazioni</Link>.</div>
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-5 mb-10">
        <div className="hm-card p-6">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Annunci</div>
          <div className="font-heading font-black text-4xl">{listings.length}</div>
        </div>
        <div className="hm-card p-6">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Visualizzazioni</div>
          <div className="font-heading font-black text-4xl text-[#FFBE00]">{listings.reduce((a, l) => a + (l.views || 0), 0)}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{listings.reduce((a, l) => a + (l.unique_views || 0), 0)} uniche</div>
        </div>
        <div className="hm-card p-6">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Click contatti</div>
          <div className="font-heading font-black text-4xl text-[#E619A8]">{listings.reduce((a, l) => a + ((l.clicks || {}).total || 0), 0)}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">email · tel · WA · web</div>
        </div>
        <div className="hm-card p-6">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Abbonamento</div>
          {active ? (
            <>
              <div className="font-heading font-black text-2xl" style={{ color: TIER_COLORS[sub.tier] }}>{TIER_LABEL[sub.tier]} · {sub.cycle === "yearly" ? "Annuale" : "Mensile"}</div>
              <div className="text-xs text-zinc-500 mt-1">Scade: {new Date(sub.expires_at).toLocaleDateString("it-IT")} <span className="text-zinc-400">({daysLeft} giorni)</span></div>
              {sub.cancel_at_period_end ? (
                <div className="mt-3">
                  <div className="text-xs text-orange-400 mb-1"><i className="fas fa-triangle-exclamation mr-1"></i>Cancellato — nessun rinnovo automatico</div>
                  <button onClick={resumeSub} className="text-xs uppercase tracking-wider px-3 py-1.5 rounded border border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-black transition" data-testid="sub-resume">Riattiva rinnovo</button>
                </div>
              ) : (
                <div className="mt-3 flex gap-2 flex-wrap">
                  <button onClick={openBillingPortal} className="text-xs uppercase tracking-wider px-3 py-1.5 rounded border border-zinc-700 hover:border-[#FFBE00] hover:text-[#FFBE00]" data-testid="sub-billing-portal">
                    <i className="fas fa-credit-card mr-1"></i> Gestisci
                  </button>
                  <Link to="/prezzi" className="text-xs uppercase tracking-wider px-3 py-1.5 rounded border border-zinc-700 hover:border-[#FFBE00] hover:text-[#FFBE00]">Cambia</Link>
                  <button onClick={cancelSub} disabled={cancelling} className="text-xs uppercase tracking-wider px-3 py-1.5 rounded border border-red-900 text-red-400 hover:border-red-500 hover:text-red-500 disabled:opacity-50" data-testid="sub-cancel">
                    {cancelling ? "…" : "Disdici"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="font-heading font-black text-2xl text-zinc-500">Nessuno</div>
              <Link to="/prezzi" className="text-xs text-[#FFBE00] hover:underline uppercase tracking-wider mt-1 inline-block">Attiva abbonamento →</Link>
            </>
          )}
        </div>
      </div>

      <section>
        <h2 className="font-heading font-bold text-xl mb-4">I tuoi annunci</h2>
        {!loading && <BoostBanner listings={listings} />}
        {loading && <div className="text-zinc-400">Caricamento…</div>}
        {!loading && listings.length === 0 && (
          <div className="hm-card p-12 text-center">
            <i className="fas fa-folder-open text-4xl text-zinc-700 mb-3"></i>
            <div className="text-zinc-400 mb-4">Non hai ancora pubblicato annunci.</div>
            <Link to="/dashboard/nuovo" className="px-4 py-2 rounded-md bg-[#E619A8] text-white text-sm font-bold uppercase">Crea il tuo primo annuncio</Link>
          </div>
        )}
        <div className="space-y-4">
          {listings.map((l) => (
            <DashboardListingCard key={l.id} listing={l} onDelete={del} />
          ))}
        </div>
      </section>
    </div>
  );
}
